import logger from '../utils/logger';
/**
 * WebSocket service for real-time notifications.
 *
 * Connects to Django Channels backend with JWT authentication.
 * Handles reconnection, heartbeat, and message routing.
 */

// WebSocket URL (use wss:// in production, ws:// in development)
const getWebSocketUrl = (token) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = process.env.REACT_APP_WS_HOST || window.location.host;
  const url = `${protocol}//${host}/ws/notifications/?token=${token}`;
  logger.log('🔌 WebSocket URL config:', {
    protocol,
    'REACT_APP_WS_HOST': process.env.REACT_APP_WS_HOST,
    'window.location.host': window.location.host,
    'final host': host,
    'full URL': url.substring(0, 80) + '...'
  });
  return url;
};

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.heartbeatInterval = null;
    this.token = null;
    this.isConnecting = false;
  }

  /**
   * Connect to WebSocket server with JWT token
   * @param {string} accessToken - JWT access token
   */
  connect(accessToken) {
    if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
      return;
    }

    this.token = accessToken;
    this.isConnecting = true;

    try {
      const url = getWebSocketUrl(accessToken);
      this.socket = new WebSocket(url);

      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
    } catch (error) {
      logger.error('WebSocket connection error:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.stopHeartbeat();
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.token = null;
    this.isConnecting = false;
  }

  /**
   * Handle WebSocket open event
   */
  handleOpen() {
    logger.log('✅ WebSocket connected successfully');
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    this.startHeartbeat();
    this.emit('connected', {});
  }

  /**
   * Handle WebSocket close event
   */
  handleClose(event) {
    logger.log('WebSocket disconnected:', event.code, event.reason);
    this.isConnecting = false;
    this.stopHeartbeat();
    this.emit('disconnected', { code: event.code, reason: event.reason });

    // Attempt reconnection if not a clean close
    if (event.code !== 1000 && event.code !== 1001) {
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket error event
   */
  handleError(error) {
    logger.error('WebSocket error:', error);
    this.isConnecting = false;
    this.emit('error', error);
  }

  /**
   * Handle incoming WebSocket message
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      const { type, ...payload } = data;

      logger.log('📨 WebSocket message received:', { type, payload });

      // Handle pong for heartbeat
      if (type === 'pong') {
        return;
      }

      // Emit event to listeners
      logger.log(`🔊 Emitting event '${type}' to listeners`);
      this.emit(type, payload);
    } catch (error) {
      logger.error('Failed to parse WebSocket message:', error);
    }
  }

  /**
   * Send message to WebSocket server
   */
  send(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      logger.warn('WebSocket not connected, cannot send message');
    }
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.log('Max reconnection attempts reached');
      this.emit('reconnect_failed', {});
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    logger.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (this.token) {
        this.reconnectAttempts++;
        this.connect(this.token);
      }
    }, delay);
  }

  /**
   * Add event listener
   * @param {string} event - Event type
   * @param {function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event type
   * @param {function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Emit event to all listeners
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          logger.error(`Error in listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Mark notification as read
   */
  markNotificationRead(notificationId) {
    this.send({ type: 'mark_read', notification_id: notificationId });
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
